import {Box, Button, Divider, Stack} from '@mui/material';
import React, {Fragment} from 'react';

import {AlertInfo, EmptySearchState, MainHeader} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';

import {AppointmentBookingEnum, WellBornTaggingEnum} from 'main/analytics/gaTagsEnum';
import {PhonesDrawer} from 'presentation/components/PhonesDrawer';
import {PhonesMenu} from 'presentation/components/PhonesMenu';
import {CopySectionMaskEnum, FeaturesEnum} from 'presentation/constants';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import type {Specialty} from 'presentation/utils/categorizeSpecialtiesByFirstLetter';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

import {BookingNotFoundStateProps} from './types';

import {useBookingNotFound} from '../../hooks/useBookingNotFound';
import {useBookingSpecialty} from '../../hooks/useBookingSpecialty';
import {GynecologistEnum} from '../../constants/beBornWell';
import {DuplicityDialog} from '../DuplicityDialog';
import {CheckBookingModalEnum} from '../../hooks/useBookingSpecialty/types';
import {PendingDialog} from '../PendingDialog';
import {FlowEnum} from '../../constants';
import {RequestSchedulePermission} from 'presentation/modules/booking/shared/ui/components/RequestSchedulePermission';

const {call, changeLocation, loading} = translations['pt-br'].commons;
const {choosePhoneNumber} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow.unitDetails;
const {changeAppointmentLocal, changeAttendanceUnit} =
	translations['pt-br'].privateFlow.bookingFlow.unitsFlow;
const {bookingGynecology} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow.notFoundModal;
const {beBornWellInfos} = translations['pt-br'].privateFlow.bookingFlow.beBornWellFlow;
const {goToUnit} = translations['pt-br'].privateFlow.bookingFlow.districtFlow.notFoundModal;
const {verifyingAuth} = translations['pt-br'].privateFlow.bookingFlow.specialtyFlow;

export function BookingNotFoundState({
	handleBack,
	pageTitle,
	variant,
	internalPadding,
	flowType,
	showHeader = true,
	dataTagId,
	healthRequestSchedulePermission,
	specialtyNotAvailableOnline,
	handleNext = () => {},
}: BookingNotFoundStateProps) {
	const {isMobile} = useMediaQueries();
	const {isEnabled: pacmanFlagIsEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_PACMAN);
	const flowTypeForBooking = flowType as Exclude<
		FlowEnum,
		FlowEnum.PROFILE | FlowEnum.AUTHORIZATIONS | FlowEnum.HEALTH_GUIDE | FlowEnum.ODONTO_GUIDE
	>;
	const allowsRequestScheduleRegistration = Boolean(
		healthRequestSchedulePermission?.allowsRegistration && pacmanFlagIsEnabled,
	);

	const {
		NOT_FOUND_CONTENT,
		handleCallChatRoom,
		handleClosePhoneMenu,
		handleOpenDrawer,
		handleOpenPhoneMenu,
		handleTogglePhoneDrawer,
		isPhoneNumberMenuOpen,
		localityButtonText,
		openPhoneDrawerState,
		showLocalityButton,
		isBeBornWellVariant,
		showCallButton,
		isChatButtonOutlined,
		showDistrictChangeLocation,
		showDistrictGoToUnit,
		phoneNumbers,
		phoneNumbersAlert,
		anchorEl,
		customBoxPadding,
		shouldShowRequestSchedulePermission,
		handleChangeLocation,
		showChangeLocationButton,
		showLocalityOrChangeLocationButton,
		showImageV2,
		callChatRoomIsLoading,
		showUnitChangeButton,
	} = useBookingNotFound({
		variant,
		flowType,
		specialtyNotAvailableOnline,
		allowsRequestScheduleRegistration,
	});

	const {
		handleSelectCard,
		handleCheckDuplicity,
		checkBookingModal,
		setCheckBookingModal,
		scheduledBooking,
	} = useBookingSpecialty({
		flowType: flowTypeForBooking,
		handleNext,
	});

	const handleGynecologyClick = async () => {
		const alreadyScheduled = await handleCheckDuplicity(GynecologistEnum.CODE);

		if (!alreadyScheduled) {
			const specialty: Specialty = {
				code: GynecologistEnum.CODE,
				description: GynecologistEnum.CLINICAL_GYNECOLOGY,
				formattedText: GynecologistEnum.CLINICAL_GYNECOLOGY,
				telemedicine: '',
			};
			await handleSelectCard(specialty);
		}
	};

	const loadingText =
		checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ? verifyingAuth : loading;

	return (
		<Fragment>
			{showHeader ? (
				<MainHeader
					title={pageTitle}
					handleBack={handleBack}
					handleClose={handleBack}
					variant="subRoute"
				/>
			) : null}
			<Box p={internalPadding ? customBoxPadding : 0}>
				<EmptySearchState
					dataTagId={dataTagId}
					title={NOT_FOUND_CONTENT[variant].title}
					description={NOT_FOUND_CONTENT[variant].description}
					imageV2={showImageV2}>
					<Stack gap={2}>
						{showLocalityOrChangeLocationButton ? (
							<Fragment>
								{showLocalityButton ? (
									<Button
										variant="contained"
										size="large"
										data-tag-id={
											AppointmentBookingEnum.CLICK_BUSCAR_UNIDADES_PROXIMAS
										}
										onClick={handleOpenDrawer}>
										{localityButtonText}
									</Button>
								) : null}
								{showChangeLocationButton ? (
									<Button
										variant="contained"
										size="large"
										data-tag-id={
											AppointmentBookingEnum.CLICK_ALTERAR_LOCAL_ATENDIMENTO_AGENDAMENTO
										}
										onClick={handleChangeLocation}>
										{changeAppointmentLocal}
									</Button>
								) : null}

								<Divider />
							</Fragment>
						) : null}

						{showDistrictGoToUnit ? (
							<Fragment>
								<Button variant="contained" size="large" onClick={handleNext}>
									{goToUnit}
								</Button>
								<Divider />
								<Button
									variant="outlined"
									size="large"
									data-tag-id={
										AppointmentBookingEnum.CLICK_ALTERAR_LOCAL_ATENDIMENTO_AGENDAMENTO
									}
									onClick={handleChangeLocation}>
									{changeAppointmentLocal}
								</Button>
							</Fragment>
						) : null}

						{showDistrictChangeLocation && !shouldShowRequestSchedulePermission ? (
							<Fragment>
								<Button
									variant="contained"
									size="large"
									data-tag-id={
										AppointmentBookingEnum.CLICK_ALTERAR_LOCAL_ATENDIMENTO_AGENDAMENTO
									}
									onClick={handleChangeLocation}>
									{changeLocation}
								</Button>
							</Fragment>
						) : null}

						{shouldShowRequestSchedulePermission && healthRequestSchedulePermission ? (
							<Fragment>
								<RequestSchedulePermission
									requestAlreadyExists={
										healthRequestSchedulePermission.requestAlreadyExists
									}
									requestCity={healthRequestSchedulePermission.requestCity}
								/>
								<Divider />
							</Fragment>
						) : null}

						{showUnitChangeButton ? (
							<Button variant="contained" size="large" onClick={handleBack}>
								{changeAttendanceUnit}
							</Button>
						) : null}

						<Button
							variant={isChatButtonOutlined ? 'outlined' : 'contained'}
							size="large"
							data-tag-id={
								AppointmentBookingEnum.CLICK_FALAR_COM_ATENDIMENTO_AGENDAMENTO
							}
							onClick={handleCallChatRoom}
							disabled={callChatRoomIsLoading}
							endIcon={
								callChatRoomIsLoading ? <Loading size={16} color="inherit" /> : null
							}>
							{NOT_FOUND_CONTENT[variant].chatButtonText}
						</Button>

						{showCallButton ? (
							<Button
								variant={'outlined'}
								size="large"
								data-tag-id={AppointmentBookingEnum.CLICK_LIGAR_AGENDAMENTO}
								onClick={handleOpenPhoneMenu}>
								{call}
							</Button>
						) : null}

						{isBeBornWellVariant ? (
							<Fragment>
								<Divider />
								<Button
									data-tag-id={
										WellBornTaggingEnum.CLICK_AGENDAR_GINECOLOGIA_CLINICA
									}
									variant="outlined"
									size="large"
									onClick={handleGynecologyClick}>
									{bookingGynecology}
								</Button>
							</Fragment>
						) : null}
					</Stack>
				</EmptySearchState>
			</Box>

			{isMobile ? (
				<PhonesDrawer.Root
					open={openPhoneDrawerState}
					toggleDrawer={handleTogglePhoneDrawer}
					title={choosePhoneNumber}>
					<AlertInfo.Root>
						{isBeBornWellVariant ? [beBornWellInfos.alert] : phoneNumbersAlert}
					</AlertInfo.Root>
					{isBeBornWellVariant ? (
						<PhonesDrawer.Simple
							mask={CopySectionMaskEnum.PHONE_WITHOUT_DDD}
							phoneNumbers={[beBornWellInfos.telephoneNumber]}
						/>
					) : (
						<PhonesDrawer.WithTitle phoneNumbers={phoneNumbers} />
					)}
				</PhonesDrawer.Root>
			) : (
				<PhonesMenu.Root
					open={isPhoneNumberMenuOpen}
					onClose={handleClosePhoneMenu}
					anchorEl={anchorEl}>
					<Box pb={2}>
						<AlertInfo.Root>
							{isBeBornWellVariant ? [beBornWellInfos.alert] : phoneNumbersAlert}
						</AlertInfo.Root>
					</Box>
					{isBeBornWellVariant ? (
						<PhonesDrawer.Simple
							mask={CopySectionMaskEnum.PHONE_WITHOUT_DDD}
							phoneNumbers={[beBornWellInfos.telephoneNumber]}
						/>
					) : (
						<PhonesDrawer.WithTitle phoneNumbers={phoneNumbers} />
					)}
				</PhonesMenu.Root>
			)}

			{scheduledBooking ? (
				<DuplicityDialog
					flowType={flowTypeForBooking}
					open={checkBookingModal === CheckBookingModalEnum.DUPLICITY_STATE}
					setOpen={() => setCheckBookingModal('none')}
					scheduledBooking={scheduledBooking}
				/>
			) : null}

			<PendingDialog
				open={
					checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ||
					checkBookingModal === CheckBookingModalEnum.VERIFYING_DUPLICITY ||
					checkBookingModal === CheckBookingModalEnum.VERIFYING_DISTRICTS ||
					checkBookingModal === CheckBookingModalEnum.VERIFYING_REQUEST_SCHEDULE
				}
				text={loadingText}
			/>
		</Fragment>
	);
}
